import React, { useState, useEffect } from "react";
import Box from "@material-ui/core/Box";
import Typography from "@material-ui/core/Typography";
import makeStyles from "@material-ui/core/styles/makeStyles";
import BackIcon from "@material-ui/icons/ArrowBack";
import IconButton from "@material-ui/core/IconButton";
import { connect } from "react-redux";
import { Link as RouterLink } from "react-router-dom";
import { useSnackbar } from "notistack";
import BlackButton from "../BlackButton";
import countries from "../../../utils/countries.json";
import API from "../../../utils/axios";
import Form from "../../common/Form";
import { trimSpaces } from "../../../utils/helpers";
import { emailRegex } from "../../../utils/regExps";
import { setBillingDetails } from "../../../store/actions/account";

const useStyles = makeStyles(theme => ({
  header: {
    minHeight: 80,
    boxSizing: "border-box",
    borderBottom: theme.variables.border
  },
  title: {
    fontWeight: 500,
    fontSize: "1.25rem",
    lineHeight: "1.4375rem"
  },
  inputWrapper: {
    boxSizing: "border-box"
  },
  countryIndicator: {
    width: theme.spacing(5),
    height: 51,
    border: "1px solid #DDD",
    backgroundColor: "#EEEEEE",
    textAlign: "center",
    borderRadius: "2px",
    marginRight: theme.spacing(2),
    fontSize: "15px",
    flexShrink: 0,
    lineHeight: "50px",
    color: "#B6B6B6",
    marginTop: theme.spacing(0.5),
    "&:hover": {
      cursor: "not-allowed"
    }
  },
  subtitle: {
    fontWeight: 500,
    fontSize: "1rem",
    lineHeight: "1.1875rem"
  },
  sectionText: {
    lineHeight: "1rem",
    marginBottom: theme.spacing(1)
  }
}));

function BillingDetails({ setLoading, billingDetails, setBillingDetails }) {
  const classes = useStyles();

  const [saveDisabled, setSaveDisabled] = useState(true);

  const generateInputValues = () => ({
    email: billingDetails.email || "",
    address: billingDetails.address_line1 || "",
    address2: billingDetails.address_line2 || "",
    zipcode: billingDetails.postal_code || "",
    city: billingDetails.city || "",
    vatID:
      billingDetails.valid_vat && billingDetails.vat_id
        ? billingDetails.vat_id.slice(2)
        : "",
    region: billingDetails.state || "",
    companyName: billingDetails.name || ""
  });

  const [inputValues, setInputValues] = useState(generateInputValues());

  useEffect(() => {
    setInputValues(generateInputValues());
    setSelectValues({
      country: billingDetails.country || ""
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [billingDetails]);

  const [errors, setErrors] = useState({
    address: "",
    region: "",
    email: "",
    city: "",
    companyName: "",
    zipcode: "",
    vatID: ""
  });

  const [selectValues, setSelectValues] = useState({
    country: billingDetails.country || ""
  });

  const handleSelect = e => {
    setSelectValues({ ...selectValues, [e.target.name]: e.target.value });
    setSaveDisabled(false);
  };

  const handleChange = e => {
    setInputValues({ ...inputValues, [e.target.name]: e.target.value });
    setSaveDisabled(false);
  };

  const isUS = selectValues.country === "US";

  const checkForErrors = () => {
    const currentErrors = {
      email: !emailRegex.test(inputValues.email.trim())
        ? "Choose a valid email address"
        : "",
      companyName: !inputValues.companyName.trim()
        ? "Please fill in this field"
        : "",
      zipcode:
        isUS && !inputValues.zipcode.trim() ? "Please fill in this field" : ""
    };
    setErrors({ ...currentErrors });
    return Object.keys(currentErrors).some(key => currentErrors[key]);
  };

  const { enqueueSnackbar } = useSnackbar();

  const handleSave = () => {
    if (checkForErrors()) {
      return;
    } else {
      setLoading(true);
      let vatID = trimSpaces(inputValues.vatID);

      if (vatID.slice(0, 2).toUpperCase() === selectValues.country)
        vatID = vatID.slice(2);

      if (vatID) {
        API.post("/v1/billing/check/vat", {
          vat_number: selectValues.country + vatID
        }).then(res => {
          if (res.data) {
            submitForm(vatID);
          } else {
            setLoading(false);
            setErrors({ ...errors, vatID: true });
          }
        });
      } else {
        submitForm(vatID);
      }
    }
  };

  const submitForm = vatID => {
    API.patch("/v1/billing/details", {
      email: inputValues.email,
      name: inputValues.companyName,
      city: inputValues.city,
      country: selectValues.country,
      address_line1: inputValues.address,
      address_line2: inputValues.address2,
      postal_code: inputValues.zipcode,
      state: inputValues.region,
      vat_number: vatID ? selectValues.country + vatID : ""
    })
      .then(res => {
        setBillingDetails(res.data);
        if (!res.data.vat_id || res.data.valid_vat) {
          // setBillingDetailsVisible(false);
        } else {
          setErrors({ ...errors, vatID: "VAT number is not valid" });
        }
        enqueueSnackbar("Billing details updated");
      })
      .catch(err => {
        console.log(err);
        if (err && err.status === 400 && err.data && err.data.errors[0]) {
          const fieldName = err.data.errors[0].field;
          const defaultMessage = err.data.errors[0].defaultMessage;
          if (fieldName === "") {
            enqueueSnackbar(defaultMessage);
            setSaveDisabled(true);
          } else {
            if (Object.keys(errors).includes(fieldName)) {
              setErrors({
                [fieldName]: defaultMessage
              });
            } else {
              enqueueSnackbar(
                defaultMessage ||
                  "Something went wrong, couldn't update billing details"
              );
            }
          }
        } else {
          enqueueSnackbar(
            "Something went wrong, couldn't update billing details"
          );
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <>
      {/* Header */}
      <Box
        className={classes.header}
        display="flex"
        alignItems="center"
        pr={3}
        pl={2}
      >
        <Box className={classes.backButton} mr={2}>
          <IconButton
            component={RouterLink}
            to="/billing/overview"
            data-cy="backToBilling"
          >
            <BackIcon />
          </IconButton>
        </Box>
        <Typography variant="h5" className={classes.title}>
          Edit billing details
        </Typography>
      </Box>

      {/* Body */}
      <Box
        p={3}
        pr={5}
        display="flex"
        flexDirection="column"
        height="100%"
        style={{ boxSizing: "border-box", overflowY: "auto" }}
      >
        <Box mt={1} mb={0.5} className={classes.formSection}>
          <Box mb={2}>
            <Typography className={classes.subtitle}>
              Billing contact
            </Typography>
          </Box>
          {/* Email */}
          <Form
            data={[
              [
                {
                  renderInputField: true,
                  label: "Email address",
                  name: "email",
                  type: "email",
                  dataCy: "email"
                }
              ]
            ]}
            handleChange={handleChange}
            inputValues={inputValues}
            errorValues={errors}
          />
        </Box>
        <Box mt={3} mb={0.5} className={classes.formSection}>
          <Box mb={2}>
            <Typography className={classes.subtitle}>
              Billing address
            </Typography>
          </Box>
          {/* Form */}
          <Form
            data={[
              [
                {
                  renderInputField: true,
                  label: "Company name",
                  name: "companyName",
                  type: "text",
                  dataCy: "companyName"
                },
                {
                  labelId: "reg-country",
                  selectId: "reg-country-select",
                  label: "Country",
                  name: "country",
                  options: countries,
                  shrink: true,
                  choose: true,
                  dataCy: "country",
                  onChange: e => handleSelect(e),
                  componentType: "select"
                }
              ],
              [
                {
                  renderInputField: true,
                  label: "Address line 1",
                  name: "address",
                  type: "text",
                  dataCy: "address"
                }
              ],
              [
                {
                  renderInputField: true,
                  label: <span>Address line 2</span>,
                  name: "address2",
                  type: "text",
                  dataCy: "address2"
                }
              ],
              [
                {
                  renderInputField: true,
                  label: "City",
                  name: "city",
                  type: "text",
                  dataCy: "city"
                }
              ],
              [
                {
                  renderInputField: true,
                  label: "State/Provice/Region",
                  name: "region",
                  type: "text",
                  dataCy: "region"
                },
                {
                  renderInputField: true,
                  label: "Zip or Postal Code",
                  name: "zipcode",
                  type: "text",
                  dataCy: "zipcode"
                }
              ]
            ]}
            handleChange={handleChange}
            selectValues={selectValues}
            inputValues={inputValues}
            errorValues={errors}
          />
        </Box>
        <Box mt={3} className={classes.formSection}>
          <Box mb={2}>
            <Typography className={classes.subtitle}>VAT Number</Typography>
          </Box>
          <Typography variant="body2" className={classes.sectionText}>
            If you have a registered business within the EU, you can use your
            VAT number to have the VAT charge deducted from your invoice. VAT
            IDs typically contain a two-letter country code following by digits
            and letters. For example, Routetitan’s VAT ID is NL 855072738B01.
          </Typography>
          <Box
            className={classes.inputWrapper}
            display="flex"
            justifyContent="space-between"
            alignItems="start"
          >
            <Box className={classes.countryIndicator}>
              {selectValues.country}
            </Box>
            {/* VAT ID */}
            <Form
              data={[
                [
                  {
                    renderInputField: true,
                    label: (
                      <span>
                        VAT Tax ID Number <i>(Optional)</i>
                      </span>
                    ),
                    name: "vatID",
                    type: "text",
                    dataCy: "vatID"
                  }
                ]
              ]}
              handleChange={handleChange}
              inputValues={inputValues}
              errorValues={errors}
            />
          </Box>
        </Box>
        {/* Actions */}
        <Box display="flex" justifyContent="flex-end" py={1} mt={3}>
          <BlackButton
            handleClick={() => handleSave()}
            disabled={saveDisabled}
            content="Save"
            data-cy="Save"
          />
        </Box>
      </Box>
    </>
  );
}

const mapStateToProps = state => ({
  billingDetails: state.account.billingDetails
});

export default connect(mapStateToProps, { setBillingDetails })(BillingDetails);
